import type { JsonObject } from 'type-fest';

import { prepareAndMakeRequest } from '@frontend/api';
import type { RequestQuery } from '@frontend/api';

// TODO: move all of this to API package
const AddOnCellsService = {
  update: (addOnCellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/add_on_cells/${addOnCellId}`,
      { body: data },
    ),
};

const AddOnsService = {
  create: (componentId: string, data: JsonObject = {}) =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/add_ons/`, {
      body: data,
    }),
  delete: (addOnId: string) =>
    prepareAndMakeRequest.delete(`/quotes/components/add_ons/${addOnId}`),
  update: (addOnId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/add_ons/${addOnId}`, {
      body: data,
    }),

  list: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/add_ons/`),
  livePricing: (addOnId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/add_ons/${addOnId}/preview/`,
      { body: data },
    ),
};

const CellsService = {
  update: (cellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/cells/${cellId}`, {
      body: data,
    }),
};

//TODO - FETCH DATA REMOVAL: Check task set
const OperationsService = {
  create: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/operations/`,
      { body: data },
    ),

  delete: (operationId: string) =>
    prepareAndMakeRequest.delete(
      `/quotes/components/operations/${operationId}`,
    ),

  list: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/operations/`),

  livePricing: (operationId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/operations/${operationId}/preview/`,
      { body: data },
    ),

  update: (operationId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/operations/${operationId}`,
      { body: data },
    ),
};

const QuantitiesService = {
  create: (componentId: string, quantity: number) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/quantities/`,
      { body: { quantity } },
    ),

  delete: (quantityId: string) =>
    prepareAndMakeRequest.delete(`/quotes/components/quantities/${quantityId}`),

  get: (quantityId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/quantities/${quantityId}`),

  list: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/quantities/`),

  update: (quantityId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/quantities/${quantityId}`, {
      body: data,
    }),

  bulkUpdate: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/${componentId}/bulk_update_quantities`,
      { body: data },
    ),

  getRealTimeMaterialPricing: (
    componentId: string,
    params: { isRound?: boolean },
  ) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/real_time_material_pricing/`,
      { query: params },
    ),
};

const ComponentsService = {
  changePurchasedComponent: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/${componentId}/update_purchased_component/`,
      { body: data },
    ),

  convertToPurchasedComponent: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/convert_to_purchased/`,
      { body: data },
    ),

  convertToManufacturedComponent: (componentId: string) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/convert_to_manufactured/`,
    ),

  bulkAddPurchasedComponents: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/add_purchased_components`,
      { body: data },
    ),

  copyToQuoteItem: (componentId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/copy_to_quote_item`,
      { body: data },
    ),

  get: (componentId: string, params?: RequestQuery) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}`, {
      query: params || {},
    }),

  getBrief: (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/brief/${componentId}`),

  interrogationRequest: (componentId: string) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/interrogation_request/`,
    ),
};

const ProfitItemCellsService = {
  update: (profitItemCellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/profit_item_cells/${profitItemCellId}`,
      { body: data },
    ),
};

const ProfitItemsService = {
  create: (componentId: string, data: JsonObject = {}) =>
    prepareAndMakeRequest.post(
      `/quotes/components/${componentId}/profit_items/`,
      { body: data },
    ),
  delete: (profitItemId: string) =>
    prepareAndMakeRequest.delete(
      `/quotes/components/profit_items/${profitItemId}`,
    ),
  update: (profitItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/profit_items/${profitItemId}`,
      { body: data },
    ),

  list: (componentId: string) =>
    prepareAndMakeRequest.get(
      `/quotes/components/${componentId}/profit_items/`,
    ),

  livePricing: (profitItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/profit_items/${profitItemId}/preview/`,
      { body: data },
    ),
};

const MultiComponentNestService = {
  list: (quoteId: string) =>
    prepareAndMakeRequest.get(
      `/quotes/nesting/${quoteId}/nestable_component_list`,
    ),

  create: (quoteId: string, payload: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/nesting/${quoteId}/multi_component_nest_request`,
      { body: payload },
    ),

  regenerateSheet: (quoteId: string, payload: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/nesting/${quoteId}/regenerate_multi_component_nest`,
      { body: payload },
    ),

  createLinear: (quoteId: string, payload: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/nesting/${quoteId}/multi_component_linear_nest_request`,
      { body: payload },
    ),

  regenerateLinear: (quoteId: string, payload: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/nesting/${quoteId}/regenerate_multi_component_linear_nest`,
      { body: payload },
    ),

  get: (nestId: string) =>
    prepareAndMakeRequest.get(`/quotes/nesting/${nestId}/nest_details`),

  getNestsStatus: (quoteId: string) =>
    prepareAndMakeRequest.get(
      `/quotes/nesting/${quoteId}/multi_component_nest_status`,
    ),

  delete: (quoteId: string, nestIds: string[]) =>
    prepareAndMakeRequest.delete(
      `/quotes/nesting/${quoteId}/bulk_delete_nests`,
      { body: { nestIds } },
    ),
};

const DiscountCellsService = {
  update: (discountCellId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(
      `/quotes/components/discount_cells/${discountCellId}`,
      { body: data },
    ),
};

const DiscountsService = {
  create: (componentId: string, data: JsonObject = {}) =>
    prepareAndMakeRequest.post(`/quotes/components/${componentId}/discounts/`, {
      body: data,
    }),

  delete: async (discountId: string) =>
    prepareAndMakeRequest.delete(`/quotes/components/discounts/${discountId}`),

  update: async (discountId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/components/discounts/${discountId}`, {
      body: data,
    }),

  list: async (componentId: string) =>
    prepareAndMakeRequest.get(`/quotes/components/${componentId}/discounts/`),

  livePricing: async (discountId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/components/discounts/${discountId}/preview/`,
      { body: data },
    ),
};

const QuoteItemService = {
  addOnCells: AddOnCellsService,
  addOns: AddOnsService,
  profitItemCells: ProfitItemCellsService,
  profitItems: ProfitItemsService,
  discountCells: DiscountCellsService,
  discounts: DiscountsService,
  cells: CellsService,
  operations: OperationsService,
  quantities: QuantitiesService,
  components: ComponentsService,
  multiNest: MultiComponentNestService,

  bulkUpdateComponents: (quoteId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/${quoteId}/components/bulk_update/`, {
      body: data,
    }),

  bulkUpdatePartNodeQuantity: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/items/${quoteItemId}/bulk_update_node_count/`,
      { body: data },
    ),

  briefComponentsList: (quoteItemId: string, componentIds?: string[]) => {
    const query: { componentIds?: string } = {};
    if (componentIds) {
      query.componentIds = componentIds.join(',');
    }
    return prepareAndMakeRequest.get(
      `/quotes/items/${quoteItemId}/brief_components/`,
      { query: query },
    );
  },

  deleteComponents: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/items/${quoteItemId}/delete_components/`,
      { body: data },
    ),

  upload: (quoteId: string, formData: FormData) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/file_upload`, {
      body: formData,
    }),

  createManualPart: (quoteId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(`/quotes/${quoteId}/create_manual_part`, {
      body: data,
    }),

  uploadComponent: (quoteItemId: string, formData: FormData) =>
    prepareAndMakeRequest.post(
      `/quotes/items/${quoteItemId}/manual_component_upload`,
      { body: formData },
    ),

  createManualComponent: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.post(
      `/quotes/items/${quoteItemId}/manual_component_create`,
      { body: data },
    ),

  setWorkflowStep: (quoteItemId: string, data: JsonObject) =>
    prepareAndMakeRequest.patch(`/quotes/items/${quoteItemId}/workflow_steps`, {
      body: data,
    }),

  refreshQuoteItems: (quoteItems: string[]) =>
    prepareAndMakeRequest.post('/quotes/items/bulk_refresh_pricing', {
      body: { quoteItems },
    }),
};

export default QuoteItemService;
